import { useEffect, useState, useRef, useCallback } from 'react'
import { useIdleTimer } from 'react-idle-timer'
import dayjs from 'dayjs'
import { EVENTS, trackEvent } from '../events'

// Session timer configuration
const DEFAULT_TIMEOUT = 1000 * 60 * 5 // 5 minutes idle timeout
const MIN_SESSION_DURATION = 300 // 5 minutes minimum session length (in seconds)
const SESSION_STORAGE_KEY = 'currentSession' // localStorage key for session persistence

/**
 * Session Timer Hook
 *
 * Tracks user session duration and logs sessionTimeout events when users go idle.
 * Handles browser refresh/close by persisting session start time to localStorage
 * and continuing the session when user returns (no duplicate events logged).
 *
 * Flow:
 * 1. User becomes active -> Start/continue session
 * 2. User goes idle (5min) -> Log sessionTimeout event
 * 3. Browser close/refresh -> Persist session start time
 * 4. User returns -> Continue interrupted session from original start time
 *
 * @param {number} timeoutMs - Idle timeout in milliseconds (default: 5 minutes)
 */
export const useSessionTimer = (timeoutMs = DEFAULT_TIMEOUT) => {
    const [initialDate, setInitialDate] = useState(null) // Session start time
    const hasLoggedSession = useRef(false) // Prevents duplicate logging

    // Calculate session duration and format as HH:MM:SS
    const calculateSessionData = useCallback((startDate, endDate = dayjs()) => {
        if (!startDate) return null
        const diffInSeconds = endDate.diff(startDate, 'second')
        // Filter out sessions shorter than minimum duration
        if (diffInSeconds < MIN_SESSION_DURATION) return null
        const hours = Math.floor(diffInSeconds / 3600)
        const minutes = Math.floor((diffInSeconds % 3600) / 60)
        const seconds = diffInSeconds % 60
        // Format duration as HH:MM:SS
        const formatted = [
            hours.toString().padStart(2, '0'),
            minutes.toString().padStart(2, '0'),
            seconds.toString().padStart(2, '0'),
        ].join(':')
        return {
            startAt: startDate.format('MM/DD/YYYY HH:mm:ss'),
            endAt: endDate.format('MM/DD/YYYY HH:mm:ss'),
            duration: formatted,
        }
    }, [])

    // Save session start time to localStorage for recovery after browser refresh/close
    const persistSessionStart = useCallback((startDate) => {
        if (!startDate) return
        try {
            localStorage.setItem(SESSION_STORAGE_KEY, startDate.toISOString())
        } catch (error) {
            console.warn('Failed to persist session start time:', error)
        }
    }, [])

    // Remove session data from localStorage and reset logging flag
    const clearPersistedSession = useCallback(() => {
        try {
            localStorage.removeItem(SESSION_STORAGE_KEY)
        } catch (error) {
            console.warn('Failed to clear session data:', error)
        }
        hasLoggedSession.current = false
    }, [])

    // Log session event to backend (disabled in dev environment)
    const logSessionWithRetry = useCallback(
        async (sessionData, eventType = EVENTS.SESSION.TIMEOUT) => {
            if (process.env.REACT_APP_SCOOP_ENV === 'dev') return true
            try {
                await trackEvent(eventType, sessionData)
                return true
            } catch (error) {
                console.error('Failed to log session:', error)
                return false
            }
        },
        []
    )

    // End the current session and log it (prevents duplicate logging)
    const endCurrentSession = useCallback(
        async (endDate = dayjs(), eventType = EVENTS.SESSION.TIMEOUT) => {
            if (!initialDate || hasLoggedSession.current) return
            const sessionData = calculateSessionData(initialDate, endDate)
            if (!sessionData) return
            hasLoggedSession.current = true
            const success = await logSessionWithRetry(sessionData, eventType)
            if (success) {
                clearPersistedSession()
            }
        },
        [initialDate, calculateSessionData, logSessionWithRetry, clearPersistedSession]
    )

    // Check for interrupted session from previous app instance and restore it
    const checkForInterruptedSession = useCallback(() => {
        try {
            const storedStartTime = localStorage.getItem(SESSION_STORAGE_KEY)
            if (!storedStartTime) return null
            const startTime = dayjs(storedStartTime)
            const timeSinceStart = dayjs().diff(startTime, 'minute')
            // Discard sessions older than 30 minutes (considered stale)
            if (timeSinceStart > 30) {
                clearPersistedSession()
                return null
            }
            // Return the interrupted session start time to continue tracking
            return startTime
        } catch (error) {
            console.warn('Failed to check for interrupted session:', error)
            clearPersistedSession()
            return null
        }
    }, [clearPersistedSession])

    // Handle user idle timeout (called by react-idle-timer)
    const handleOnIdle = useCallback(async () => {
        // Subtract the idle timeout to exclude AFK time from session duration
        const endDate = dayjs().subtract(timeoutMs, 'milliseconds')
        await endCurrentSession(endDate, EVENTS.SESSION.TIMEOUT)
    }, [timeoutMs, endCurrentSession])

    const handleOnActive = useCallback(() => {
        // Check for any interrupted session from previous app instance
        const restoredSessionStart = checkForInterruptedSession()
        if (restoredSessionStart) {
            // Continue the interrupted session
            setInitialDate(restoredSessionStart)
            hasLoggedSession.current = false
            // Keep the existing localStorage entry until session completes normally
        } else {
            // Start new session
            const newSessionStart = dayjs()
            setInitialDate(newSessionStart)
            hasLoggedSession.current = false
            persistSessionStart(newSessionStart)
        }
    }, [checkForInterruptedSession, persistSessionStart])

    // Initialize session tracking when component mounts
    useEffect(() => {
        handleOnActive()
    }, [handleOnActive])

    // Handle page unload - log session immediately on browser/tab close
    useEffect(() => {
        const handleBeforeUnload = () => {
            // Log the session immediately when browser/tab closes
            if (initialDate && !hasLoggedSession.current) {
                endCurrentSession(dayjs(), EVENTS.SESSION.CLOSE)
            }
        }
        const handleVisibilityChange = () => {
            // Save session start time when page becomes hidden
            if (document.visibilityState === 'hidden' && initialDate && !hasLoggedSession.current) {
                persistSessionStart(initialDate)
            }
        }
        window.addEventListener('beforeunload', handleBeforeUnload)
        document.addEventListener('visibilitychange', handleVisibilityChange)
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload)
            document.removeEventListener('visibilitychange', handleVisibilityChange)
        }
    }, [initialDate, persistSessionStart, endCurrentSession])

    // Configure idle timer to detect user inactivity
    useIdleTimer({
        timeout: timeoutMs,
        onIdle: handleOnIdle,
        onActive: handleOnActive,
        debounce: 300, // Wait 300ms before considering user active again
    })
}
